import { FilterOperator, useAdminSearchRoomsQuery } from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  type DataGridProps,
  type GridEventListener,
  type GridRowParams,
  NoRowsOverlay,
  PlaceOutlinedIcon,
} from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';

import { BaseExamLocationDataGrid } from '../../components';
import { useAdminExamLocationListSearchParams } from '../../hooks';
import { type ExamLocationRow } from '../../types';

import { adminExamCenterLocationColumns } from './columns';

export default function AdminExamCenterLocationList() {
  const navigate = useNavigate();

  const [
    { page, pageSize, sortOrder, locationName, provinceId },
    { changePageNumber, changePageSize, changeSorting },
  ] = useAdminExamLocationListSearchParams();

  const { data: searchRoomsData, loading } = useAdminSearchRoomsQuery({
    variables: {
      searchRoomsInput: {
        paging: {
          pageSize,
          currentPage: page,
        },
        order: {
          field: 'location',
          type: ORDER_TYPE[sortOrder],
        },
        filter: {
          searchText: {
            fields: ['location'],
            value: locationName,
          },
          conditions: [
            {
              field: 'provinceId',
              operator: FilterOperator.Equal,
              value: provinceId,
            },
            {
              field: 'status',
              operator: FilterOperator.Equal,
              value: 'ACTIVE',
            },
          ],
        },
      },
    },
  });

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<ExamLocationRow>,
  ) => {
    navigate('/admin/exam-location/locations/:roomId', {
      params: {
        roomId: params.row.id,
      },
    });
  };

  const handleSortModelChange: DataGridProps['onSortModelChange'] = (sortModel) => {
    const sort = sortModel[0]?.sort ?? 'asc';
    changeSorting({ sortOrder: sort });
  };

  const rows: ExamLocationRow[] =
    searchRoomsData?.ecAdminSearchRooms.rooms.map((room) => ({
      id: room.id,
      locationName: room.building.location.nameTh ?? '',
      province: room.building.location.province.nameTh,
      buildingName: room.building.nameTh,
      roomName: room.name,
      floor: room.floor,
      roomNumber: room.roomNumber ?? '-',
      approvedAt: room.updatedAt ? new Date(room.updatedAt) : null,
    })) ?? [];
  const total = searchRoomsData?.ecAdminSearchRooms.total;
  const isSearchResultEmpty = total === 0 && (locationName !== '' || provinceId !== '');

  const shouldHideFooter = loading || total === 0;
  return (
    <BaseExamLocationDataGrid
      rows={rows}
      columns={adminExamCenterLocationColumns}
      hideFooter={shouldHideFooter}
      onRowClick={handleRowClick}
      sortingMode='server'
      initialState={{
        sorting: {
          sortModel: [{ field: 'locationName', sort: sortOrder }],
        },
      }}
      onSortModelChange={handleSortModelChange}
      loading={loading}
      slotProps={{
        footer: {
          total: total ?? 0,
          pagination: {
            page: page,
            count: total ?? 0,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              changePageNumber(0);
              return changePageSize(pageSize);
            },
          },
        },
      }}
      slots={{
        noRowsOverlay: () =>
          !isSearchResultEmpty ? (
            <NoRowsOverlay icon={PlaceOutlinedIcon} title='ยังไม่มีข้อมูลสถานที่สอบ' />
          ) : (
            <NoRowsOverlay />
          ),
      }}
    />
  );
}
